/**
 * Adapter hook for listing equipment with pagination, filtering, and cache control.
 * Wraps useListEquipmentsQuery and transforms GraphQL DTOs to EquipmentTableData for DataTable consumption.
 */
import {
  Dtos_V1_Equipment_EquipmentDtoFilterInput,
  QueryEquipmentsArgs,
  useListEquipmentsQuery
} from '@/common/infrastructure/gql/generated/schema';

import { EquipmentTableData } from '../types/EquipmentTypes';
import { FilterOption } from '@/common/presentation/components/EntityFilterSubmenu';
import { mapEquipmentToTableData } from '../mappers/EquipmentMappers';

export const useListEquipments = (filters: QueryEquipmentsArgs, cachePolicy: 'cache-first' | 'no-cache' = 'cache-first') => {
  const { data, loading, error, refetch } = useListEquipmentsQuery({
    variables: filters,
    fetchPolicy: cachePolicy
  });

  const equipments: EquipmentTableData[] = data?.equipments?.items
    ? data.equipments.items.map((item) => mapEquipmentToTableData(item))
    : [];
  const pageInfo = data?.equipments?.pageInfo;
  const totalCount = data?.equipments?.totalCount;

  return { equipments, pageInfo, totalCount, loading, error, refetch };
};

/**
 * Helper function to add filter conditions to the AND array
 * @param and - The AND conditions array to modify
 * @param filters - Array of filter options
 * @param fieldPath - The field path in the filter object
 * @param useId - Whether to use nested id structure (default: true)
 */
const addFilterCondition = (
  and: Dtos_V1_Equipment_EquipmentDtoFilterInput[],
  filters: FilterOption[],
  fieldPath: string,
  useId: boolean = true
) => {
  const selectedIds = filters.filter((f) => f.checked).map((f) => f.id);
  if (selectedIds.length > 0) {
    const condition = useId ? { [fieldPath]: { id: { in: selectedIds } } } : { [fieldPath]: { in: selectedIds } };
    and.push(condition);
  }
};

/**
 * Helper function to add filter conditions using OR logic for individual equality checks
 * @param and - The AND conditions array to modify
 * @param filters - Array of filter options
 * @param fieldPath - The field path in the filter object
 * @param useId - Whether to use nested id structure (default: true)
 */
const addOrFilterCondition = (
  and: Dtos_V1_Equipment_EquipmentDtoFilterInput[],
  filters: FilterOption[],
  fieldPath: string,
  useId: boolean = true
) => {
  const selectedIds = filters.filter((f) => f.checked).map((f) => f.id);
  if (selectedIds.length > 0) {
    if (selectedIds.length === 1) {
      // Single selection - use direct equality
      const condition = useId ? { [fieldPath]: { id: { eq: selectedIds[0] } } } : { [fieldPath]: { eq: selectedIds[0] } };
      and.push(condition);
    } else {
      // Multiple selections - use OR logic
      const orConditions = selectedIds.map((id) => (useId ? { [fieldPath]: { id: { eq: id } } } : { [fieldPath]: { eq: id } }));
      and.push({ or: orConditions });
    }
  }
};

const buildEquipmentSearchOrConditions = (term: string): Dtos_V1_Equipment_EquipmentDtoFilterInput[] => {
  const or: Dtos_V1_Equipment_EquipmentDtoFilterInput[] = [
    { name: { contains: term } },
    { notes: { contains: term } },
    { vinNumber: { contains: term } },
    { gpsTrackerSerialNumber: { contains: term } },
    { machineSerialNumber: { contains: term } },
    { boomSerialNumber: { contains: term } },
    { boomModelNum: { contains: term } },
    { truckModelNumber: { contains: term } }
  ];
  const num = parseInt(term, 10);
  if (!isNaN(num) && String(num) === term) {
    or.push({ id: { eq: num } });
  }
  return or;
};

export function buildEquipmentListWhereFilter(
  searchTerm: string,
  makeFilters: FilterOption[],
  modelFilters: FilterOption[],
  yearFilters: FilterOption[],
  classFilters: FilterOption[],
  statusFilters: FilterOption[]
) {
  const and: Dtos_V1_Equipment_EquipmentDtoFilterInput[] = [];

  if (searchTerm.trim()) {
    and.push({ or: buildEquipmentSearchOrConditions(searchTerm.trim()) });
  }

  addFilterCondition(and, makeFilters, 'make');
  addFilterCondition(and, modelFilters, 'model');
  addOrFilterCondition(and, yearFilters, 'year', false);
  addFilterCondition(and, classFilters, 'class');
  addOrFilterCondition(and, statusFilters, 'status');

  if (and.length > 0) {
    return { and };
  }
  return {};
}

/**
 * Builds filter for OUT OF SERVICE tab with AND logic
 */
export function buildEquipmentOutOfServiceWhereFilter(
  searchTerm: string,
  makeFilters: FilterOption[],
  modelFilters: FilterOption[],
  yearFilters: FilterOption[],
  classFilters: FilterOption[],
  statusFilters: FilterOption[]
) {
  const and: Dtos_V1_Equipment_EquipmentDtoFilterInput[] = [];

  if (searchTerm.trim()) {
    and.push({ or: buildEquipmentSearchOrConditions(searchTerm.trim()) });
  }

  addFilterCondition(and, makeFilters, 'make');
  addFilterCondition(and, modelFilters, 'model');
  addOrFilterCondition(and, yearFilters, 'year', false);
  addFilterCondition(and, classFilters, 'class');
  addOrFilterCondition(and, statusFilters, 'status');

  if (and.length > 0) {
    return { and };
  }
  return {};
}
